import axios from "axios";
import type { Transition, Variants } from "motion/react";

import { config } from "@krea/shared/config";
import { CountryCode } from "@krea/shared/types/common";

import { DEFAULT_FIELD_REQUESTS } from "@/utils/fieldRequests/defaultFieldRequests";

import {
  FieldRequestFieldEnum,
  LenderApplicationsFilterEnum,
} from "./features/common/enums";

const pageVariants: Variants = {
  initial: {
    opacity: 0,
    x: "-100vw",
  },
  animate: {
    opacity: 1,
    x: 0,
  },
  exit: {
    x: "100vw",
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

const pageTransition: Transition = {
  type: "tween",
  duration: 0.3,
  ease: "easeOut",
};

const loginVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  exit: {
    opacity: 0,
    scale: 0,
    transition: {
      duration: 0.2,
    },
  },
};
const settings = {
  environment: config.environment,
  kreaBaseApiUrl: config.apiRootUrl,
  customerWebUrlByCountry: {
    [CountryCode.SE]: config.appUrls(CountryCode.SE).customer,
    [CountryCode.FI]: config.appUrls(CountryCode.FI).customer,
  },
  keaLenderIntegrationApiDocsUrl: config.lenderApiDocsUrl,
  defaultFilters: [
    LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
    LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
    LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
    LenderApplicationsFilterEnum.NO_LEASING_BIDS,
  ],
  defaultListLimit: 10,
  defaultListSort: {
    [LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS]:
      "loan_application__created_at:asc",
    [LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS]:
      "loan_application__created_at:asc",
    [LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS]:
      "loan_application__created_at:asc",
    [LenderApplicationsFilterEnum.NO_LEASING_BIDS]:
      "loan_application__created_at:asc",
    [LenderApplicationsFilterEnum.HAS_OWN_BIDS]:
      "loan_application__created_at:desc",
    [LenderApplicationsFilterEnum.BID_SELECTED]: "id:desc",
    [LenderApplicationsFilterEnum.PAID_OUT]:
      "loan_application__payout_marked_as_received_at:desc",
    [LenderApplicationsFilterEnum.REJECTED]:
      "loan_application__created_at:desc",
    [LenderApplicationsFilterEnum.CLOSED_BY_CUSTOMER]:
      "marked_eligible_for_lender_at:desc",
  },
  defaultApiLogsLimit: 100,

  defaultFieldRequestsForLoans: [
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.guarantors],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.parent_company_guarantee],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.property],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.company_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.share_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.pawned_invoices],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.reason_explanation],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.accounting_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.annual_report],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.banking_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.budget],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.additional_securities],
  ],
  defaultFieldRequestsForFactoring: [
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.guarantors],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.parent_company_guarantee],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.property],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.company_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.share_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.pawned_invoices],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.reason_explanation],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.accounting_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.annual_report],
    DEFAULT_FIELD_REQUESTS[
      FieldRequestFieldEnum.unpaid_invoices_additional_data
    ],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.banking_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.additional_securities],
  ],
  defaultFieldRequestsForRealEstate: [
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.guarantors],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.parent_company_guarantee],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.property],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.company_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.share_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.pawned_invoices],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.reason_explanation],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.accounting_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.annual_report],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.banking_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.property_additional_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.construction_estimate],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.budget],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.building_permit],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.property_cash_flow],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.additional_securities],
  ],
  defaultFieldRequestsForLeasing: [
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.guarantors],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.parent_company_guarantee],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.property],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.company_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.share_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.pawned_invoices],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.reason_explanation],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.accounting_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.annual_report],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.banking_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.leasing_additional_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.budget],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.additional_securities],
  ],
  defaultFieldRequestsForRevolvingCredit: [
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.guarantors],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.parent_company_guarantee],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.property],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.company_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.share_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.pawned_invoices],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.reason_explanation],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.accounting_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.annual_report],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.banking_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.budget],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.additional_securities],
  ],
  defaultFieldRequestsForPurchaseFinancing: [
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.guarantors],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.parent_company_guarantee],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.property],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.company_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.share_mortgage],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.pawned_invoices],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.reason_explanation],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.accounting_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.annual_report],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.banking_data],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.budget],
    DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.additional_securities],
  ],
  routeAnimation: {
    pageVariants,
    pageTransition,
  },
  loginAnimation: {
    variants: loginVariants,
  },
};

// Set axios config
axios.defaults.baseURL = settings.kreaBaseApiUrl;

// eslint-disable-next-line import/no-default-export
export default settings;
